import { Injectable, signal, computed, inject } from "@angular/core";
import { UserDataModel } from "@app/settings-interfaces";
import type { CardsByCardIdMap, TaxprepT1Card, JobsBillingCard } from "@app/views-interfaces";
import { type ICardsCountByUser, makeCardsCount, ICardsCount, IRoleUserMapping } from "@interfaces/index";
import { DashboardsService } from "@shared/dashboards-service/dashboards.service";
import { isUnassigned } from "@shared/users-service/users.service";
import { MANAGER_ROLE_ID, PARTNER_ROLE_ID } from "@shared/ifirm-roles-service/ifirm-roles.service";

// Per-J&B-field count keys stored inside ICardsCount.totalByRole alongside the numeric iFirm role IDs.
// Chosen so they differ from role IDs "1" (Manager) and "2" (Partner).
export const JOB_MANAGER_COUNT_KEY = "job.manager";
export const JOB_PARTNER_COUNT_KEY = "job.partner";
export const JOB_OWNER_COUNT_KEY = "job.owner";
export const JOB_CURRENTLY_RESPONSIBLE_COUNT_KEY = "job.currentlyResponsible";

@Injectable({ providedIn: "root" })
export class CardsService {
    private readonly dashboardsService = inject(DashboardsService);
    cards = signal<CardsByCardIdMap>(new Map());

    readonly cardsByUsers = computed<ICardsCountByUser>(
        () => {
            const cards = Array.from(this.cards()?.values() ?? []);
            const isJobsBilling = this.dashboardsService.currentDashboardIsJobsBilling();
            const cardsByUsers: ICardsCountByUser = new Map();

            if (!cards || !Array.isArray(cards) || !cards.length) return cardsByUsers;

            for (const card of cards) {
                if (!isJobsBilling) {
                    const typedCard = card as TaxprepT1Card;
                    for (const role of typedCard.roleUserMapping ?? []) {
                        const userId = role?.userId;
                        if (isUnassigned(userId)) continue;
                        const user = cardsByUsers.get(userId) ?? makeCardsCount();
                        user.total++;

                        const key = this.normalizeRoleId(role?.roleId);
                        if (key !== null) {
                            user.totalByRole[key] = (user.totalByRole[key] ?? 0) + 1;
                        } else console.warn("[WK] roleId is missing for the user", userId);
                        cardsByUsers.set(userId, user);
                    }
                } else {
                    const typedCard = card as JobsBillingCard;
                    if (typedCard.contactInformation.manager) {
                        const userId = String(typedCard.contactInformation.manager);
                        if (isUnassigned(userId)) continue;
                        const user = cardsByUsers.get(userId) ?? makeCardsCount();
                        user.total++;
                        user.totalByRole[MANAGER_ROLE_ID] = (user.totalByRole[MANAGER_ROLE_ID] ?? 0) + 1;
                        cardsByUsers.set(userId, user);
                    }

                    if (typedCard.contactInformation.partner) {
                        const userId = String(typedCard.contactInformation.partner);
                        if (isUnassigned(userId)) continue;
                        const user = cardsByUsers.get(userId) ?? makeCardsCount();
                        user.total++;
                        user.totalByRole[PARTNER_ROLE_ID] = (user.totalByRole[PARTNER_ROLE_ID] ?? 0) + 1;
                        cardsByUsers.set(userId, user);
                    }

                    if (typedCard.job.manager) {
                        const userId = String(typedCard.job.manager);
                        if (isUnassigned(userId)) continue;
                        const user = cardsByUsers.get(userId) ?? makeCardsCount();
                        user.total++;
                        user.totalByRole[JOB_MANAGER_COUNT_KEY] = (user.totalByRole[JOB_MANAGER_COUNT_KEY] ?? 0) + 1;
                        cardsByUsers.set(userId, user);
                    }

                    if (typedCard.job.partner) {
                        const userId = String(typedCard.job.partner);
                        if (isUnassigned(userId)) continue;
                        const user = cardsByUsers.get(userId) ?? makeCardsCount();
                        user.total++;

                        user.totalByRole[JOB_PARTNER_COUNT_KEY] = (user.totalByRole[JOB_PARTNER_COUNT_KEY] ?? 0) + 1;
                        cardsByUsers.set(userId, user);
                    }

                    if (typedCard.job.owner) {
                        const userId = String(typedCard.job.owner);
                        if (isUnassigned(userId)) continue;
                        const user = cardsByUsers.get(userId) ?? makeCardsCount();
                        user.total++;
                        user.totalByRole[JOB_OWNER_COUNT_KEY] = (user.totalByRole[JOB_OWNER_COUNT_KEY] ?? 0) + 1;
                        cardsByUsers.set(userId, user);
                    }

                    if (typedCard.job.currentlyResponsible) {
                        const userId = String(typedCard.job.currentlyResponsible);
                        if (isUnassigned(userId)) continue;
                        const user = cardsByUsers.get(userId) ?? makeCardsCount();
                        user.total++;
                        user.totalByRole[JOB_CURRENTLY_RESPONSIBLE_COUNT_KEY] = (user.totalByRole[JOB_CURRENTLY_RESPONSIBLE_COUNT_KEY] ?? 0) + 1;
                        cardsByUsers.set(userId, user);
                    }
                }
            }

            return cardsByUsers;
        },
        { equal: (a, b) => this.cardsByUsersEqual(a, b) },
    );

    private cardsByUsersEqual(a: ICardsCountByUser, b: ICardsCountByUser): boolean {
        if (a === b) return true;
        if (a?.size !== b?.size) return false;
        for (const [userId, aCount] of a) {
            const bCount = b.get(userId);
            if (aCount.total !== bCount?.total) return false;
            const aKeys = Object.keys(aCount.totalByRole);
            if (aKeys.length !== Object.keys(bCount.totalByRole).length) return false;
            for (const key of aKeys) {
                if (aCount.totalByRole[key] !== bCount.totalByRole[key]) return false;
            }
        }
        return true;
    }

    normalizeRoleId(id: unknown): string | null {
        if (typeof id !== "string") return null;
        const s = id.trim();
        return s.length ? s.toLowerCase() : null;
    }

    private combineCardsCount(a: ICardsCount, b: ICardsCount): ICardsCount {
        const total = (a.total ?? 0) + (b.total ?? 0);

        const aRoles = a.totalByRole ?? {};
        const bRoles = b.totalByRole ?? {};

        const totalByRole: IRoleUserMapping = Object.create(null);

        for (const k in aRoles) {
            if (!Object.hasOwn(aRoles, k)) continue;
            const v = aRoles[k];
            if (typeof v === "number" && Number.isFinite(v)) {
                totalByRole[k] = v;
            }
        }

        for (const k in bRoles) {
            if (!Object.hasOwn(bRoles, k)) continue;
            const vb = bRoles[k];
            if (typeof vb === "number" && Number.isFinite(vb)) {
                totalByRole[k] = (totalByRole[k] ?? 0) + vb;
            }
        }

        return { total, totalByRole };
    }

    getCardsCountForUser(user: UserDataModel): ICardsCount {
        const cardsByUsers = this.cardsByUsers();
        return this.combineCardsCount(cardsByUsers.get(user.Id) ?? makeCardsCount(), cardsByUsers.get(user.IFirmUserId) ?? makeCardsCount());
    }
}
